import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useDebounce } from '../../utils/debounce';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Tooltip,
    TextField,
    Card,
    CardContent,
    Alert,
    InputAdornment,
    Snackbar,
    Breadcrumbs,
    Link,
    Paper,
    Chip,
    Stack,
    Skeleton,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Popover,
    Badge,
    Divider,
    Avatar,
    tooltipClasses,
    TooltipProps,
    Autocomplete
} from '@mui/material';
import {
    Search,
    Edit,
    Delete,
    ArrowBack,
    NavigateNext,
    HowToVote,
    Person,
    FilterList,
    Add,
    RestartAlt,
    Close,
    Download,
    CloudUpload,
    Print,
    PersonAdd
} from '@mui/icons-material';
import CitizenDetailsDialog from '../../components/admin/CitizenDetailsDialog';
import ImportProgressDialog, { ImportPhase } from '../../components/common/ImportProgressDialog';
import { Checkbox, FormControlLabel } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams, GridRowSelectionModel, GridRowId } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import api from '../../api/axios';
import AssignSubLeaderDialog from '../../components/admin/AssignSubLeaderDialog';
import { useAuth } from '../../contexts/AuthContext';

// --- Interfaces ---
import { Citizen } from '../../types/citizen';
import { VotingBooth } from '../../types/voting_booth';
import { SubLeader } from '../../types/sub_leader';

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.black,
    },
}));

interface FilterState {
    gender: string;
    min_age: string;
    max_age: string;
    community: string;
    profession: string;
    blood_group: string;
    is_active_social: boolean | null;
    is_nri: boolean | null;
    is_resident_satys: boolean | null;
}

const VotingBoothDetails = () => {
    const { constituencyId, villageId, boothId } = useParams<{ constituencyId: string; villageId: string; boothId: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { hasPermission } = useAuth();

    // State
    const [pageModel, setPageModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    const [viewDialog, setViewDialog] = useState<{ open: boolean; citizen: any | null }>({
        open: false,
        citizen: null
    });
    // Assign Sub Leader Dialog
    const [assignDialog, setAssignDialog] = useState<{ open: boolean; citizen: any | null }>({
        open: false,
        citizen: null
    });

    // Import Logic
    const [importState, setImportState] = useState<{
        active: boolean;
        fileName: string;
        uploadPct: number;
        phase: ImportPhase;
    }>({ active: false, fileName: '', uploadPct: 0, phase: 'uploading' });

    const handleImportClick = () => {
        // Guard against a second file dialog while one import is already in flight.
        if (importState.active) return;
        document.getElementById('csv-file-input')?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const input = event.target;
        const file = input.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('constituency_id', constituencyId || '');
        formData.append('village_id', villageId || '');
        formData.append('voting_booth_id', boothId || '');

        setImportState({ active: true, fileName: file.name, uploadPct: 0, phase: 'uploading' });

        try {
            const response = await api.post('/tenant/citizens/import', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (event) => {
                    if (!event.total) return;
                    const uploadPct = Math.round((event.loaded * 100) / event.total);
                    // Once the bytes are all sent the server starts writing rows, which
                    // reports no progress of its own — switch to the indeterminate phase.
                    setImportState((prev) => ({
                        ...prev,
                        uploadPct,
                        phase: uploadPct >= 100 ? 'processing' : 'uploading',
                    }));
                },
            });
            setSnackbar({
                open: true,
                message: response.data?.message || 'Import completed',
                severity: 'success'
            });
            queryClient.invalidateQueries({ queryKey: ['citizens', boothId] });
        } catch (error: any) {
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Import failed',
                severity: 'error'
            });
        } finally {
            setImportState({ active: false, fileName: '', uploadPct: 0, phase: 'uploading' });
            // Reset input so re-picking the same file fires onChange again
            input.value = '';
        }
    };

    // Filter State
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [filters, setFilters] = useState<FilterState>({
        gender: '',
        min_age: '',
        max_age: '',
        community: '',
        profession: '',
        blood_group: '',
        is_active_social: null,
        is_nri: null,
        is_resident_satys: null
    });

    // Count active filters for badge
    const activeFilterCount = Object.values(filters).filter(val => val !== '' && val !== null && val !== false).length;

    // Dialog State
    const [openDialog, setOpenDialog] = useState(false);
    const [editingItem, setEditingItem] = useState<Citizen | null>(null);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [formData, setFormData] = useState({
        name: '',
        voter_id: '',
        gender: 'male',
        age: '',
        phone: '',
        adhaar_number: '',
        community: '',
        whatsapp_number: '',
        profession: '',
        income: '',
        pro_party: '',
        dob: '',
        blood_group: '',
        is_active_social: false,
        is_nri: false,
        is_resident_satys: false,
        address: ''
    });
    const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set() });
    const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
    const [assignSubLeaderDialogOpen, setAssignSubLeaderDialogOpen] = useState(false);
    const [selectedSubLeader, setSelectedSubLeader] = useState<number | ''>('');

    // Fetch Booth Details
    const { data: booth, isLoading: isLoadingBooth } = useQuery({
        queryKey: ['voting-booth', boothId],
        queryFn: async () => {
            const res = await api.get(`/tenant/voting-booths/${boothId}`);
            return res.data?.data || res.data;
        },
        enabled: !!boothId
    });

    // Fetch Citizens
    const { data: citizensData, isLoading: citizensLoading } = useQuery({
        queryKey: ['citizens', boothId, pageModel.page, pageModel.pageSize, debouncedSearch, filters],
        queryFn: async () => {
            const params: any = {
                page: pageModel.page + 1,
                per_page: pageModel.pageSize,
                voting_booth_id: boothId,
                ...filters
            };
            if (debouncedSearch) params.search = debouncedSearch;

            // Clean up null/empty filters
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null) {
                    delete params[key];
                }
            });

            const res = await api.get('/tenant/citizens', { params });
            return res.data;
        },
        enabled: !!boothId,
        placeholderData: keepPreviousData,
    });

    // Fetch Sub Leaders for assignment
    const { data: subLeadersData } = useQuery({
        queryKey: ['sub-leaders', boothId],
        queryFn: async () => {
            const res = await api.get('/tenant/sub-leaders', {
                params: { voting_booth_id: boothId, per_page: 100 }
            });
            return res.data?.data || res.data;
        },
        enabled: !!boothId
    });

    // Fetch Communities Options
    const { data: communitiesOptions } = useQuery({
        queryKey: ['communities-options'],
        queryFn: async () => {
            const res = await api.get('/tenant/options/communities');
            return res.data?.data || [];
        }
    });

    // Fetch Professions Options
    const { data: professionsOptions } = useQuery({
        queryKey: ['professions-options'],
        queryFn: async () => {
            const res = await api.get('/tenant/options/professions');
            return res.data?.data || [];
        }
    });

    // Fetch Political Parties Options
    const { data: politicalPartiesOptions } = useQuery({
        queryKey: ['political-parties-options'],
        queryFn: async () => {
            const res = await api.get('/tenant/options/political-parties');
            return res.data?.data || [];
        }
    });

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleMutationError = (err: any) => {
        if (err.response?.status === 422 && err.response?.data?.errors) {
            const newErrors: Record<string, string> = {};
            Object.entries(err.response.data.errors).forEach(([key, msgs]: [string, any]) => {
                const msg = Array.isArray(msgs) ? msgs[0] : msgs;
                if (['name', 'voter_id', 'gender', 'age', 'phone', 'adhaar_number', 'community', 'whatsapp_number', 'profession', 'income', 'dob', 'blood_group'].includes(key)) {
                    newErrors[key] = msg;
                } else {
                    showSnackbar(msg, 'error');
                }
            });
            setFormErrors(newErrors);
        } else {
            showSnackbar(err.response?.data?.message || 'An error occurred', 'error');
        }
    };

    // Create Mutation
    const createMutation = useMutation({
        mutationFn: async (data: any) => api.post('/tenant/citizens', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['citizens', boothId] });
            handleClose();
            showSnackbar('Citizen created successfully', 'success');
        },
        onError: handleMutationError
    });

    // Update Mutation
    const updateMutation = useMutation({
        mutationFn: async (data: any) => api.put(`/tenant/citizens/${data.id}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['citizens', boothId] });
            handleClose();
            showSnackbar('Citizen updated successfully', 'success');
        },
        onError: handleMutationError
    });

    // Delete Mutation
    const deleteMutation = useMutation({
        mutationFn: async (id: number) => api.delete(`/tenant/citizens/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['citizens', boothId] });
            showSnackbar('Citizen deleted successfully', 'success');
        },
        onError: (err: any) => showSnackbar(err.response?.data?.message || 'Failed to delete citizen', 'error')
    });

    // Bulk Delete Mutation
    const bulkDeleteMutation = useMutation({
        mutationFn: async (ids: number[]) => api.post('/tenant/citizens/bulk-delete', { ids }),
        onSuccess: (response: any) => {
            queryClient.invalidateQueries({ queryKey: ['citizens', boothId] });
            setBulkDeleteDialogOpen(false);
            setSelectedRows({ type: 'include', ids: new Set() });
            showSnackbar(response.data?.message || 'Citizens deleted successfully', 'success');
        },
        onError: (err: any) => showSnackbar(err.response?.data?.message || 'Failed to delete citizens', 'error')
    });

    // Assign Sub Leader Mutation
    const assignSubLeaderMutation = useMutation({
        mutationFn: async ({ citizenIds, subLeaderId }: { citizenIds: number[]; subLeaderId: number }) =>
            api.post('/tenant/citizens/assign-sub-leader', { citizen_ids: citizenIds, sub_leader_id: subLeaderId }),
        onSuccess: (response: any) => {
            queryClient.invalidateQueries({ queryKey: ['citizens', boothId] });
            setAssignSubLeaderDialogOpen(false);
            setSelectedSubLeader('');
            setSelectedRows({ type: 'include', ids: new Set() });
            showSnackbar(response.data?.message || 'Sub Leader assigned successfully', 'success');
        },
        onError: (err: any) => showSnackbar(err.response?.data?.message || 'Failed to assign sub leader', 'error')
    });

    const handleOpen = (item?: Citizen) => {
        if (item) {
            setEditingItem(item);
            setFormData({
                name: item.name,
                voter_id: item.voter_id || '',
                gender: (item.gender?.toLowerCase() || 'male') as any,
                age: item.age ? String(item.age) : '',
                phone: item.phone || item.mobile_number || '',
                adhaar_number: item.adhaar_number || '',
                community: item.community || '',
                whatsapp_number: item.whatsapp_number || '',
                profession: item.profession || '',
                income: item.income || '',
                pro_party: item.pro_party || '',
                dob: item.dob || '',
                blood_group: item.blood_group || '',
                is_active_social: Boolean(item.is_active_social),
                is_nri: Boolean(item.is_nri),
                is_resident_satys: Boolean(item.is_resident_satys),
                address: item.address || ''
            });
        } else {
            setEditingItem(null);
            setFormData({
                name: '',
                voter_id: '',
                gender: 'male',
                age: '',
                phone: '',
                adhaar_number: '',
                community: '',
                whatsapp_number: '',
                profession: '',
                income: '',
                pro_party: '',
                dob: '',
                blood_group: '',
                is_active_social: false,
                is_nri: false,
                is_resident_satys: false,
                address: ''
            });
        }
        setFormErrors({});
        setOpenDialog(true);
    };

    const handleClose = () => {
        setOpenDialog(false);
        setEditingItem(null);
        setFormErrors({});
    };

    const handleSubmit = () => {
        const errors: any = {};
        if (!formData.name) errors.name = 'Name is required';
        if (!formData.gender) errors.gender = 'Gender is required';
        if (!formData.voter_id) errors.voter_id = 'Voter ID is required';

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        const payload = {
            ...formData,
            constituency_id: Number(constituencyId),
            village_id: Number(villageId),
            voting_booth_id: Number(boothId),
            age: formData.age ? Number(formData.age) : null,
            is_active_social: formData.is_active_social ? 1 : 0,
            is_nri: formData.is_nri ? 1 : 0,
            is_resident_satys: formData.is_resident_satys ? 1 : 0,
        };

        if (editingItem) {
            updateMutation.mutate({ ...payload, id: editingItem.id });
        } else {
            createMutation.mutate(payload);
        }
    };

    const handleDelete = (id: number) => {
        if (window.confirm('Are you sure you want to delete this citizen? This action cannot be undone.')) {
            deleteMutation.mutate(id);
        }
    };

    // Filter Handlers
    const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleFilterClose = () => {
        setAnchorEl(null);
    };

    const handleFilterChange = (field: keyof FilterState, value: any) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleResetFilters = () => {
        setFilters({
            gender: '',
            min_age: '',
            max_age: '',
            community: '',
            profession: '',
            blood_group: '',
            is_active_social: null,
            is_nri: null,
            is_resident_satys: null
        });
    };

    const handleExport = () => {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        if (constituencyId) params.append('constituency_id', constituencyId);
        if (villageId) params.append('village_id', villageId);
        if (boothId) params.append('voting_booth_id', boothId);

        Object.entries(filters).forEach(([key, value]) => {
            if (value !== null && value !== '') {
                params.append(key, String(value));
            }
        });

        api.get(`/tenant/citizens/export?${params.toString()}`, { responseType: 'blob' })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `citizens_export_${boothId || 'all'}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            })
            .catch((err) => {
                setSnackbar({
                    open: true,
                    message: 'Failed to export CSV',
                    severity: 'error'
                });
            });
    };

    // Helper for professional consistent colors
    const stringToColor = (string: string) => {
        const colors = [
            ['#0f766e', '#ccfbf1'], // Teal
            ['#4338ca', '#e0e7ff'], // Indigo
            ['#1d4ed8', '#dbeafe'], // Blue
            ['#7c3aed', '#ede9fe'], // Violet
            ['#be185d', '#fce7f3'], // Pink
            ['#b45309', '#fef3c7'], // Amber
        ];
        let hash = 0;
        for (let i = 0; i < string.length; i += 1) {
            hash = string.charCodeAt(i) + ((hash << 5) - hash);
        }
        const index = Math.abs(hash) % colors.length;
        return colors[index];
    };

    const handleOpenViewDialog = (citizen: any) => {
        setViewDialog({ open: true, citizen });
    };

    const handleCloseViewDialog = () => {
        setViewDialog({ open: false, citizen: null });
    };

    const handleOpenAssignDialog = (citizen: any) => {
        setAssignDialog({ open: true, citizen });
    };

    const handleCloseAssignDialog = () => {
        setAssignDialog({ open: false, citizen: null });
    };

    // Columns
    const columns: GridColDef[] = [
        {
            field: 'index',
            headerName: '#',
            width: 60,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.7, fontFamily: 'Inter, sans-serif' }}>
                    {(pageModel.page * pageModel.pageSize) + params.api.getAllRowIds().indexOf(params.row.id) + 1}
                </Typography>
            ),
        },
        {
            field: 'voter_id',
            headerName: 'Voter ID',
            width: 140,
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    size="small"
                    variant="outlined"
                    sx={{
                        borderRadius: 1.5,
                        fontWeight: 600,
                        borderColor: 'divider',
                        bgcolor: 'rgba(0,0,0,0.01)',
                        color: 'text.primary',
                        fontFamily: 'monospace'
                    }}
                />
            )
        },
        {
            field: 'name',
            headerName: 'Citizen',
            flex: 1,
            minWidth: 200,
            renderCell: (params) => {
                const [darkColor, lightColor] = stringToColor(params.value || 'Unknown');
                return (
                    <Box
                        onClick={() => handleOpenViewDialog(params.row)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            cursor: 'pointer',
                            p: 0.5,
                            borderRadius: 2,
                            width: '100%',
                            transition: 'background-color 0.2s',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 40,
                                height: 40,
                                bgcolor: lightColor,
                                color: darkColor,
                                fontWeight: 700,
                                fontSize: '0.95rem',
                                border: `1px solid ${darkColor}20`
                            }}
                        >
                            {params.value ? params.value.charAt(0).toUpperCase() : '?'}
                        </Avatar>
                        <Typography variant="body2" fontWeight={600} sx={{ color: '#0f172a', letterSpacing: '-0.01em', textDecoration: 'underline', textDecorationColor: 'transparent', transition: 'text-decoration-color 0.2s', '&:hover': { textDecorationColor: 'inherit' } }}>
                            {params.value}
                        </Typography>
                    </Box>
                );
            }
        },
        { field: 'age', headerName: 'Age', width: 80 },
        {
            field: 'gender',
            headerName: 'Gender',
            width: 100,
            valueFormatter: (value: string) => value ? value.charAt(0).toUpperCase() + value.slice(1) : ''
        },
        { field: 'phone', headerName: 'Mobile', width: 130 },
        { field: 'whatsapp_number', headerName: 'WhatsApp', width: 120 },
        { field: 'adhaar_number', headerName: 'Aadhaar', width: 130 },
        { field: 'community', headerName: 'Community', width: 120 },
        { field: 'profession', headerName: 'Profession', width: 120 },
        { field: 'income', headerName: 'Income', width: 100 },
        {
            field: 'pro_party',
            headerName: 'Pro Party',
            width: 140,
            renderCell: (params) => {
                if (!params.value) return '';
                const party = politicalPartiesOptions?.find((p: any) => p.name === params.value);
                const color = party?.color_code;
                return (
                    <Chip
                        label={params.value}
                        size="small"
                        sx={{
                            fontWeight: 600,
                            bgcolor: color ? `${color}20` : '#f3f4f6',
                            color: color || '#374151',
                            border: color ? `1px solid ${color}50` : '1px solid #e5e7eb',
                        }}
                    />
                );
            },
        },
        { field: 'dob', headerName: 'DOB', width: 100 },
        { field: 'blood_group', headerName: 'Blood', width: 70 },
        {
            field: 'is_active_social',
            headerName: 'Active Social',
            width: 100,
            renderCell: (params) => params.value ? <Chip label="Yes" size="small" color="success" /> : <Chip label="No" size="small" variant="outlined" />
        },
        {
            field: 'is_nri',
            headerName: 'NRI',
            width: 70,
            renderCell: (params) => params.value ? <Chip label="Yes" size="small" color="info" /> : <Chip label="No" size="small" variant="outlined" />
        },
        {
            field: 'is_resident_satys',
            headerName: 'Resident Stays',
            width: 110,
            renderCell: (params) => params.value ? <Chip label="Yes" size="small" color="warning" /> : <Chip label="No" size="small" variant="outlined" />
        },
        { field: 'address', headerName: 'Address', width: 150 },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    <IconButton onClick={() => handleOpen(params.row)} size="small" color="primary">
                        <Edit fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(params.row.id)} size="small" color="error">
                        <Delete fontSize="small" />
                    </IconButton>
                    <Tooltip title="Assign Sub Leader">
                        <IconButton onClick={() => handleOpenAssignDialog(params.row)} size="small" color="warning">
                            <Person fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        },
    ];

    // Safe total count access
    const totalCitizens = citizensData?.data?.total || citizensData?.meta?.total || 0;
    const citizensList = citizensData?.data?.data || citizensData?.data || [];

    return (
        <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
            {/* Breadcrumbs */}
            <Box sx={{ mb: 3 }}>
                <Breadcrumbs separator={<NavigateNext fontSize="small" />} aria-label="breadcrumb">
                    <Link component={RouterLink} color="inherit" to="/admin/citizens" sx={{ display: 'flex', alignItems: 'center' }}>
                        Constituencies
                    </Link>
                    <Link component={RouterLink} color="inherit" to={`/admin/citizens/${constituencyId}`} sx={{ display: 'flex', alignItems: 'center' }}>
                        {booth?.village?.constituency?.name || 'Constituency'}
                    </Link>
                    <Link component={RouterLink} color="inherit" to={`/admin/citizens/${constituencyId}/villages/${villageId}`} sx={{ display: 'flex', alignItems: 'center' }}>
                        {booth?.village?.name || 'Village'}
                    </Link>
                    <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
                        {booth?.name || 'Loading...'}
                    </Typography>
                </Breadcrumbs>
            </Box>

            {/* Header Section */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)', // Indigo gradient
                    color: 'white',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2
                }}
            >
                <Box>
                    <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton
                            onClick={() => navigate(`/admin/citizens/${constituencyId}/villages/${villageId}`)}
                            sx={{ color: 'white' }}
                        >
                            <ArrowBack />
                        </IconButton>
                        {booth?.name || <Skeleton width={200} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, opacity: 0.9, ml: 7 }}>
                        Booth ID: {booth?.booth_id}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                    <Chip
                        label={`${totalCitizens} Citizens`}
                        color="default"
                        variant="outlined"
                        sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}
                    />
                </Stack>
            </Paper>

            {/* Filters & Search */}
            <Card sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent sx={{ p: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
                    <TextField
                        size="small"
                        placeholder="Search citizens (Name, Voter ID, Mobile)..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ width: { xs: '100%', sm: 400 } }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
                        }}
                    />
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Badge badgeContent={activeFilterCount} color="primary">
                            <Button
                                variant={activeFilterCount > 0 ? "contained" : "outlined"}
                                color={activeFilterCount > 0 ? "primary" : "inherit"}
                                startIcon={<FilterList />}
                                onClick={handleFilterClick}
                            >
                                Filters
                            </Button>
                        </Badge>
                        <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'block' } }} />
                        <Button
                            variant="outlined"
                            startIcon={importState.active
                                ? <CircularProgress size={18} color="inherit" />
                                : <CloudUpload />}
                            onClick={handleImportClick}
                            disabled={importState.active}
                            sx={{ mr: 1 }}
                        >
                            {importState.active ? 'Importing…' : 'Import'}
                        </Button>
                        <input
                            type="file"
                            id="csv-file-input"
                            style={{ display: 'none' }}
                            accept=".csv"
                            onChange={handleFileChange}
                        />
                        {hasPermission('reports.export') && (
                            <Button
                                variant="outlined"
                                startIcon={<Download />}
                                onClick={handleExport}
                                sx={{ mr: 1 }}
                            >
                                Export
                            </Button>
                        )}
                        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
                            Add Citizen
                        </Button>
                        {selectedRows.ids.size > 0 && (
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<Delete />}
                                onClick={() => setBulkDeleteDialogOpen(true)}
                            >
                                Delete ({selectedRows.ids.size})
                            </Button>
                        )}
                        {selectedRows.ids.size > 0 && (
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<PersonAdd />}
                                onClick={() => setAssignSubLeaderDialogOpen(true)}
                            >
                                Assign Sub Leader ({selectedRows.ids.size})
                            </Button>
                        )}
                    </Box>
                </CardContent>
            </Card>

            {/* Filter Popover */}
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleFilterClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: { width: 400, p: 2 }
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold">Filters</Typography>
                    <Stack direction="row" spacing={1}>
                        <Button
                            size="small"
                            startIcon={<RestartAlt />}
                            onClick={handleResetFilters}
                            disabled={activeFilterCount === 0}
                        >
                            Reset
                        </Button>
                        <IconButton size="small" onClick={handleFilterClose}>
                            <Close fontSize="small" />
                        </IconButton>
                    </Stack>
                </Box>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Gender</InputLabel>
                            <Select
                                value={filters.gender}
                                label="Gender"
                                onChange={(e) => handleFilterChange('gender', e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="male">Male</MenuItem>
                                <MenuItem value="female">Female</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Blood Group</InputLabel>
                            <Select
                                value={filters.blood_group}
                                label="Blood Group"
                                onChange={(e) => handleFilterChange('blood_group', e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="A+">A+</MenuItem>
                                <MenuItem value="A-">A-</MenuItem>
                                <MenuItem value="B+">B+</MenuItem>
                                <MenuItem value="B-">B-</MenuItem>
                                <MenuItem value="AB+">AB+</MenuItem>
                                <MenuItem value="AB-">AB-</MenuItem>
                                <MenuItem value="O+">O+</MenuItem>
                                <MenuItem value="O-">O-</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <TextField
                            label="Min Age"
                            type="number"
                            size="small"
                            fullWidth
                            value={filters.min_age}
                            onChange={(e) => handleFilterChange('min_age', e.target.value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <TextField
                            label="Max Age"
                            type="number"
                            size="small"
                            fullWidth
                            value={filters.max_age}
                            onChange={(e) => handleFilterChange('max_age', e.target.value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            label="Community"
                            size="small"
                            fullWidth
                            value={filters.community}
                            onChange={(e) => handleFilterChange('community', e.target.value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            label="Profession"
                            size="small"
                            fullWidth
                            value={filters.profession}
                            onChange={(e) => handleFilterChange('profession', e.target.value)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 1 }} />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={filters.is_active_social === true}
                                    indeterminate={filters.is_active_social === null}
                                    onChange={(e) => {
                                        // Tri-state logic: null -> true -> false -> null
                                        if (filters.is_active_social === null) handleFilterChange('is_active_social', true);
                                        else if (filters.is_active_social === true) handleFilterChange('is_active_social', false);
                                        else handleFilterChange('is_active_social', null);
                                    }}
                                />
                            }
                            label="Active Socially"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={filters.is_nri === true}
                                    indeterminate={filters.is_nri === null}
                                    onChange={(e) => {
                                        if (filters.is_nri === null) handleFilterChange('is_nri', true);
                                        else if (filters.is_nri === true) handleFilterChange('is_nri', false);
                                        else handleFilterChange('is_nri', null);
                                    }}
                                />
                            }
                            label="NRI"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={filters.is_resident_satys === true}
                                    indeterminate={filters.is_resident_satys === null}
                                    onChange={(e) => {
                                        if (filters.is_resident_satys === null) handleFilterChange('is_resident_satys', true);
                                        else if (filters.is_resident_satys === true) handleFilterChange('is_resident_satys', false);
                                        else handleFilterChange('is_resident_satys', null);
                                    }}
                                />
                            }
                            label="Resident Stays"
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Button fullWidth variant="contained" onClick={handleFilterClose}>
                            Apply Filters
                        </Button>
                    </Grid>
                </Grid>
            </Popover>

            {/* Data Table */}
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <DataGrid
                    rows={citizensList}
                    columns={columns}
                    loading={citizensLoading}
                    paginationModel={pageModel}
                    onPaginationModelChange={setPageModel}
                    pageSizeOptions={[10, 25, 50, 100]}
                    paginationMode="server"
                    rowCount={totalCitizens}
                    autoHeight
                    checkboxSelection
                    disableRowSelectionOnClick
                    rowSelectionModel={selectedRows}
                    onRowSelectionModelChange={(newSelection) => {
                        if (newSelection.type === 'exclude') {
                            const currentIds = new Set<GridRowId>(citizensList.map((row: any) => row.id));
                            setSelectedRows({ type: 'include', ids: currentIds });
                        } else {
                            setSelectedRows(newSelection);
                        }
                    }}
                    sx={{
                        border: 0,
                        '& .MuiDataGrid-columnHeaders': {
                            bgcolor: '#f8fafc',
                            fontWeight: 'bold'
                        },
                        '& .MuiDataGrid-cell': {
                            borderColor: '#f1f5f9',
                        },
                        '& .MuiDataGrid-row:hover': {
                            bgcolor: '#f8fafc',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            borderTop: '1px solid #f1f5f9',
                        }
                    }}
                />
            </Card>

            {/* CRUD Dialog */}
            <Dialog open={openDialog} onClose={handleClose} maxWidth="md" fullWidth fullScreen={window.innerWidth < 600}>
                <DialogTitle>{editingItem ? 'Edit Citizen' : 'Add New Citizen'}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Name"
                                fullWidth
                                required
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                error={!!formErrors.name}
                                helperText={formErrors.name}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Voter ID"
                                fullWidth
                                required
                                value={formData.voter_id}
                                onChange={(e) => setFormData({ ...formData, voter_id: e.target.value })}
                                error={!!formErrors.voter_id}
                                helperText={formErrors.voter_id}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth error={!!formErrors.gender}>
                                <InputLabel>Gender</InputLabel>
                                <Select
                                    value={formData.gender}
                                    label="Gender"
                                    onChange={(e) => setFormData({ ...formData, gender: e.target.value as any })}
                                >
                                    <MenuItem value="male">Male</MenuItem>
                                    <MenuItem value="female">Female</MenuItem>
                                    <MenuItem value="other">Other</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Age"
                                type="number"
                                fullWidth
                                value={formData.age}
                                onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                                error={!!formErrors.age}
                                helperText={formErrors.age}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Mobile Number"
                                fullWidth
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                error={!!formErrors.phone}
                                helperText={formErrors.phone}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Adhaar Number"
                                fullWidth
                                value={formData.adhaar_number}
                                onChange={(e) => setFormData({ ...formData, adhaar_number: e.target.value })}
                                error={!!formErrors.adhaar_number}
                                helperText={formErrors.adhaar_number}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Autocomplete
                                freeSolo
                                options={communitiesOptions?.map((c: any) => c.name) || []}
                                value={formData.community}
                                onChange={(event, newValue) => {
                                    setFormData({ ...formData, community: newValue || '' });
                                }}
                                onInputChange={(event, newInputValue) => {
                                    setFormData({ ...formData, community: newInputValue });
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Community"
                                        fullWidth
                                        variant="outlined"
                                        error={!!formErrors.community}
                                        helperText={formErrors.community}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="WhatsApp Number"
                                fullWidth
                                value={formData.whatsapp_number}
                                onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
                                error={!!formErrors.whatsapp_number}
                                helperText={formErrors.whatsapp_number}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Autocomplete
                                freeSolo
                                options={professionsOptions?.map((p: any) => p.name) || []}
                                value={formData.profession}
                                onChange={(event, newValue) => {
                                    setFormData({ ...formData, profession: newValue || '' });
                                }}
                                onInputChange={(event, newInputValue) => {
                                    setFormData({ ...formData, profession: newInputValue });
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Profession"
                                        fullWidth
                                        variant="outlined"
                                        error={!!formErrors.profession}
                                        helperText={formErrors.profession}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Income"
                                fullWidth
                                value={formData.income}
                                onChange={(e) => setFormData({ ...formData, income: e.target.value })}
                                error={!!formErrors.income}
                                helperText={formErrors.income}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Autocomplete
                                freeSolo
                                options={politicalPartiesOptions?.map((p: any) => p.name) || []}
                                value={formData.pro_party}
                                onChange={(event, newValue) => {
                                    setFormData({ ...formData, pro_party: newValue || '' });
                                }}
                                onInputChange={(event, newInputValue) => {
                                    setFormData({ ...formData, pro_party: newInputValue });
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Political Party (Pro Party)"
                                        fullWidth
                                        variant="outlined"
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel>Blood Group</InputLabel>
                                <Select
                                    value={formData.blood_group}
                                    label="Blood Group"
                                    onChange={(e) => setFormData({ ...formData, blood_group: e.target.value })}
                                >
                                    <MenuItem value="">None</MenuItem>
                                    <MenuItem value="A+">A+</MenuItem>
                                    <MenuItem value="A-">A-</MenuItem>
                                    <MenuItem value="B+">B+</MenuItem>
                                    <MenuItem value="B-">B-</MenuItem>
                                    <MenuItem value="AB+">AB+</MenuItem>
                                    <MenuItem value="AB-">AB-</MenuItem>
                                    <MenuItem value="O+">O+</MenuItem>
                                    <MenuItem value="O-">O-</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Date of Birth"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={formData.dob}
                                onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                                error={!!formErrors.dob}
                                helperText={formErrors.dob}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.is_active_social}
                                            onChange={(e) => setFormData({ ...formData, is_active_social: e.target.checked })}
                                        />
                                    }
                                    label="Active Socially"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.is_nri}
                                            onChange={(e) => setFormData({ ...formData, is_nri: e.target.checked })}
                                        />
                                    }
                                    label="NRI"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.is_resident_satys}
                                            onChange={(e) => setFormData({ ...formData, is_resident_satys: e.target.checked })}
                                        />
                                    }
                                    label="Resident Stays"
                                />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                label="Address"
                                fullWidth
                                multiline
                                rows={2}
                                value={formData.address}
                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={createMutation.isPending || updateMutation.isPending}
                    >
                        {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save Citizen'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Delete Confirmation Dialog */}
            <Dialog open={bulkDeleteDialogOpen} onClose={() => setBulkDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 'bold' }}>
                    Confirm Bulk Delete
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ mt: 2 }}>
                        Are you sure you want to delete <strong>{selectedRows.ids.size} citizen(s)</strong>? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setBulkDeleteDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => bulkDeleteMutation.mutate(Array.from(selectedRows.ids) as number[])}
                        disabled={bulkDeleteMutation.isPending}
                        startIcon={<Delete />}
                    >
                        {bulkDeleteMutation.isPending ? 'Deleting...' : `Delete ${selectedRows.ids.size} Citizens`}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Assign Sub Leader Dialog */}
            <AssignSubLeaderDialog
                open={assignDialog.open}
                onClose={handleCloseAssignDialog}
                citizen={assignDialog.citizen}
                onSuccess={() => {
                    setSnackbar({ open: true, message: 'Sub leader assigned successfully', severity: 'success' });
                    queryClient.invalidateQueries({ queryKey: ['citizens', boothId] });
                }}
            />

            {/* CSV import progress */}
            <ImportProgressDialog
                open={importState.active}
                fileName={importState.fileName}
                uploadPct={importState.uploadPct}
                phase={importState.phase}
                title="Importing citizens"
            />

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
            {/* View Citizen Dialog */}
            <CitizenDetailsDialog
                open={viewDialog.open}
                onClose={handleCloseViewDialog}
                citizen={viewDialog.citizen}
            />
            {/* Assign Sub Leader Dialog */}
            <Dialog open={assignSubLeaderDialogOpen} onClose={() => setAssignSubLeaderDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: '#e0e7ff', color: '#3730a3', fontWeight: 'bold' }}>
                    Assign Sub Leader
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ mt: 2, mb: 3 }}>
                        Select a sub leader to assign to <strong>{selectedRows.ids.size} citizen(s)</strong>.
                    </Typography>
                    <FormControl fullWidth>
                        <InputLabel>Sub Leader</InputLabel>
                        <Select
                            value={selectedSubLeader}
                            label="Sub Leader"
                            onChange={(e) => setSelectedSubLeader(e.target.value as number)}
                        >
                            {(subLeadersData?.data || subLeadersData || []).map((subLeader: SubLeader) => (
                                <MenuItem key={subLeader.id} value={subLeader.id}>
                                    {subLeader.first_name} {subLeader.last_name} - {subLeader.phone}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => {
                        setAssignSubLeaderDialogOpen(false);
                        setSelectedSubLeader('');
                    }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            if (selectedSubLeader) {
                                assignSubLeaderMutation.mutate({
                                    citizenIds: Array.from(selectedRows.ids) as number[],
                                    subLeaderId: selectedSubLeader as number
                                });
                            }
                        }}
                        disabled={!selectedSubLeader || assignSubLeaderMutation.isPending}
                        startIcon={<PersonAdd />}
                    >
                        {assignSubLeaderMutation.isPending ? 'Assigning...' : 'Assign Sub Leader'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default VotingBoothDetails;
